/**
 * useColumnPreferences — Spaltensichtbarkeit + Profile + Kompaktmodus
 *
 * Persistenz: localStorage, per user-id gescoped.
 * Keine Serverabhaengigkeit — Profile bleiben lokal pro Browser/User.
 *
 * Gibt folgende API zurueck:
 *   - visible:          Set<string> sichtbarer Spaltenschluessel
 *   - toggleColumn(k):  Spalte ein/aus
 *   - hideColumn(k):    nur ausblenden
 *   - resetToDefault(): alle sichtbar, Kompakt aus
 *   - compact:          bool — dichter Modus
 *   - setCompact(b)
 *   - profiles:         [{id, name, visible:string[], compact}]
 *   - activeProfileId:  string | null
 *   - applyProfile(id)
 *   - saveCurrentAs(name)
 *   - overwriteProfile(id)
 *   - renameProfile(id, name)
 *   - deleteProfile(id)
 *   - setDefaultProfile(id) — dieses Profil beim Login laden
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const COMPACT_LS_KEY   = "stundenkonten_compact";
const PROFILES_LS_KEY  = "stundenkonten_col_profiles";
const VISIBLE_LS_KEY   = "stundenkonten_col_visible";
const ACTIVE_LS_KEY    = "stundenkonten_active_profile";
const DEFAULT_LS_KEY   = "stundenkonten_default_profile";

function scopedKey(base, userId) {
  return userId ? `${base}__${userId}` : base;
}

function readJSON(key, fallback) {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
}
function writeJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota */ }
}

/** Built-in, nicht-loeschbare Profile */
export function buildBuiltInProfiles(allKeys, travelKeys) {
  const travel = new Set(travelKeys);
  const soll = new Set(["soll", "diff"]);
  // Minimal: nur Datum + Status + Projekt + Arbeitszeit
  const MINIMAL = allKeys.filter(k => ["wt", "act", "datum", "status", "projekt", "arbeit"].includes(k));
  // Standard: ohne Fahrt-Spalten
  const STANDARD = allKeys.filter(k => !travel.has(k));
  // Erweitert: alle
  const ERWEITERT = [...allKeys];

  return [
    { id: "builtin_minimal",   name: "Minimal",   visible: MINIMAL,   compact: true,  builtin: true },
    { id: "builtin_standard",  name: "Standard",  visible: STANDARD,  compact: false, builtin: true },
    { id: "builtin_erweitert", name: "Erweitert", visible: ERWEITERT, compact: false, builtin: true },
  ];
}

export function useColumnPreferences({ userId, allKeys, travelKeys, roleDefaults }) {
  // roleDefaults: optional { hiddenKeys: string[] } — z.B. Nachunternehmer sieht keine Fahrt/Soll
  const defaultVisible = useMemo(() => {
    const hidden = new Set(roleDefaults?.hiddenKeys || []);
    return new Set(allKeys.filter(k => !hidden.has(k)));
  }, [allKeys, roleDefaults]);

  const builtIns = useMemo(() => buildBuiltInProfiles(allKeys, travelKeys), [allKeys, travelKeys]);

  // State
  const [visible, setVisible]   = useState(() => defaultVisible);
  const [compact, setCompactSt] = useState(() => readJSON(scopedKey(COMPACT_LS_KEY, userId), false));
  const [profiles, setProfiles] = useState(() => readJSON(scopedKey(PROFILES_LS_KEY, userId), []));
  const [activeProfileId, setActiveProfileId] = useState(() =>
    readJSON(scopedKey(ACTIVE_LS_KEY, userId), null)
  );
  const [defaultProfileId, setDefaultProfileId] = useState(() =>
    readJSON(scopedKey(DEFAULT_LS_KEY, userId), null)
  );

  // Re-Init wenn User wechselt (Admin-Ansicht eines anderen MA)
  useEffect(() => {
    const storedVisible = readJSON(scopedKey(VISIBLE_LS_KEY, userId), null);
    // Merge: jede neue Spalte (z.B. vma/nacht/unterkunft/abger nach Update) wird automatisch
    // sichtbar, statt fuer Bestandsuser zu fehlen, weil ihr alter LS-Eintrag sie nicht kennt.
    let nextVisible;
    if (storedVisible) {
      const merged = new Set(storedVisible);
      const known = new Set(storedVisible);
      for (const k of allKeys) if (!known.has(k)) merged.add(k);
      // remove keys no longer in allKeys
      for (const k of Array.from(merged)) if (!allKeys.includes(k)) merged.delete(k);
      nextVisible = merged;
    } else {
      nextVisible = new Set(defaultVisible);
    }
    setVisible(nextVisible);
    setCompactSt(readJSON(scopedKey(COMPACT_LS_KEY, userId), false));
    const userProfiles = readJSON(scopedKey(PROFILES_LS_KEY, userId), []);
    setProfiles(userProfiles);
    const storedActive = readJSON(scopedKey(ACTIVE_LS_KEY, userId), null);
    const storedDefault = readJSON(scopedKey(DEFAULT_LS_KEY, userId), null);
    setActiveProfileId(storedActive);
    setDefaultProfileId(storedDefault);
    // Apply default profile on first mount if none active
    if (!storedActive && storedDefault) {
      const all = [...builtIns, ...userProfiles];
      const p = all.find(x => x.id === storedDefault);
      if (p) {
        setVisible(new Set(p.visible));
        setCompactSt(!!p.compact);
        setActiveProfileId(p.id);
      }
    }
  }, [userId, builtIns, defaultVisible, allKeys]);

  // Persist visible whenever it changes
  useEffect(() => {
    writeJSON(scopedKey(VISIBLE_LS_KEY, userId), Array.from(visible));
  }, [visible, userId]);

  // Persist compact
  const setCompact = useCallback((b) => {
    setCompactSt(b);
    writeJSON(scopedKey(COMPACT_LS_KEY, userId), b);
  }, [userId]);

  const toggleColumn = useCallback((key) => {
    setVisible(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setActiveProfileId(null); // Aenderung => kein aktives Profil mehr
    writeJSON(scopedKey(ACTIVE_LS_KEY, userId), null);
  }, [userId]);

  const hideColumn = useCallback((key) => {
    setVisible(prev => {
      const next = new Set(prev); next.delete(key); return next;
    });
    setActiveProfileId(null);
    writeJSON(scopedKey(ACTIVE_LS_KEY, userId), null);
  }, [userId]);

  const resetToDefault = useCallback(() => {
    setVisible(new Set(defaultVisible));
    setCompact(false);
    setActiveProfileId(null);
    writeJSON(scopedKey(ACTIVE_LS_KEY, userId), null);
  }, [defaultVisible, setCompact, userId]);

  const applyProfile = useCallback((id) => {
    const all = [...builtIns, ...profiles];
    const p = all.find(x => x.id === id);
    if (!p) return;
    setVisible(new Set(p.visible));
    setCompact(!!p.compact);
    setActiveProfileId(p.id);
    writeJSON(scopedKey(ACTIVE_LS_KEY, userId), p.id);
  }, [builtIns, profiles, setCompact, userId]);

  const persistProfiles = useCallback((next) => {
    setProfiles(next);
    writeJSON(scopedKey(PROFILES_LS_KEY, userId), next);
  }, [userId]);

  const saveCurrentAs = useCallback((name) => {
    const cleaned = (name || "").trim();
    if (!cleaned) return null;
    const id = `user_${Date.now()}`;
    const p = { id, name: cleaned, visible: Array.from(visible), compact, builtin: false };
    persistProfiles([...profiles, p]);
    setActiveProfileId(id);
    writeJSON(scopedKey(ACTIVE_LS_KEY, userId), id);
    return id;
  }, [visible, compact, profiles, persistProfiles, userId]);

  const overwriteProfile = useCallback((id) => {
    const next = profiles.map(p => p.id === id && !p.builtin
      ? { ...p, visible: Array.from(visible), compact }
      : p);
    persistProfiles(next);
  }, [profiles, persistProfiles, visible, compact]);

  const renameProfile = useCallback((id, name) => {
    const cleaned = (name || "").trim();
    if (!cleaned) return;
    const next = profiles.map(p => p.id === id && !p.builtin ? { ...p, name: cleaned } : p);
    persistProfiles(next);
  }, [profiles, persistProfiles]);

  const deleteProfile = useCallback((id) => {
    const next = profiles.filter(p => p.id !== id);
    persistProfiles(next);
    if (activeProfileId === id) {
      setActiveProfileId(null);
      writeJSON(scopedKey(ACTIVE_LS_KEY, userId), null);
    }
    if (defaultProfileId === id) {
      setDefaultProfileId(null);
      writeJSON(scopedKey(DEFAULT_LS_KEY, userId), null);
    }
  }, [profiles, persistProfiles, activeProfileId, defaultProfileId, userId]);

  const setDefaultProfile = useCallback((id) => {
    setDefaultProfileId(id);
    writeJSON(scopedKey(DEFAULT_LS_KEY, userId), id);
  }, [userId]);

  return {
    // visibility
    visible, toggleColumn, hideColumn, resetToDefault,
    // compact
    compact, setCompact,
    // profiles
    profiles, builtIns,
    activeProfileId, applyProfile,
    saveCurrentAs, overwriteProfile, renameProfile, deleteProfile,
    defaultProfileId, setDefaultProfile,
  };
}
